// src/store/reducers/reducerHelpers/thunkGet.ts
import axiosClientCached from '@lib/axiosClientCached.ts';
import { processStoreError } from '@lib/errorHandler'; // Import the error processor
import { selectCurrentCompanySlug } from '@store/reducers/meReducer.ts';
import { addErrorResponseNotification } from '@store/reducers/notificationReducer.ts';
import createDebouncedAsyncThunk from '@store/reducers/reducerHelpers/createDebouncedAsyncThunk.ts';
import {
  defaultTypeDebounceTimeMs,
  type InterfaceFunctionReplace,
  type InterfaceVarReplace,
  replaceUrlState,
  replaceUrlVariable,
  safeDispatchActions,
} from '@store/reducers/reducerHelpers/thunkHelperShared.ts';

import type { DispatchActionType } from '@/types/DispatchActionType.ts';

export interface InterfaceThunkGetData<U = undefined | InterfaceVarReplace> {
  urlVarsReplaceMap?: U;
}

const genericTypeThunkGetReplace = <U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  replaceMap: InterfaceFunctionReplace = {},
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
  debounceTimeMs?: number,
) => {
  debounceTimeMs = debounceTimeMs ?? defaultTypeDebounceTimeMs;
  return createDebouncedAsyncThunk(
    `${type}/get/${url}`,
    async (data: InterfaceThunkGetData<U> | undefined, { rejectWithValue, dispatch, getState }) => {
      let fullUrl = replaceUrlState(url, replaceMap, getState);
      if (data?.urlVarsReplaceMap !== undefined) {
        fullUrl = replaceUrlVariable(fullUrl, data?.urlVarsReplaceMap ?? {});
      }
      const res = await axiosClientCached.get(fullUrl).catch((error) => {
        dispatch(addErrorResponseNotification({ error, rejectWithValue: undefined })); // rejectWithValue not used by notification here
        safeDispatchActions(dispatch, errorDispatchArray);
        const processedError = processStoreError(error);
        throw rejectWithValue({
          error: {
            message: processedError.message,
            statusCode: processedError.statusCode,
            data: processedError.data,
          },
        });
      });
      safeDispatchActions(dispatch, successDispatchArray);
      return res.data.data;
    },
    debounceTimeMs,
  );
};

const genericTypeThunkGet = <U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
  debounceTimeMs?: number,
) => genericTypeThunkGetReplace<U>(type, url, {}, successDispatchArray, errorDispatchArray, debounceTimeMs);

const genericTypeThunkGetCompany = <U = undefined | InterfaceVarReplace>(
  type: string,
  url: string,
  successDispatchArray: DispatchActionType[] = [],
  errorDispatchArray: DispatchActionType[] = [],
  debounceTimeMs?: number,
) =>
  genericTypeThunkGetReplace<U>(
    type,
    url,
    {
      '{companySlug}': selectCurrentCompanySlug,
    },
    successDispatchArray,
    errorDispatchArray,
    debounceTimeMs,
  );

export const thunkGet = {
  replUrl: genericTypeThunkGetReplace,
  compUrl: genericTypeThunkGetCompany,
  get: genericTypeThunkGet,
};

export default thunkGet;

//TODO: See iof we can add a default for current delivery uuid
